/**
 * Rebuild Migration Mappings
 * This script recreates missing customer migration mappings
 */

import { directSupabase } from '../lib/supabaseClient';

interface QuoteDockCustomer {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  title?: string;
  phone_number: string;
  email: string | null;
  address: string;
  billing_address: string | null;
  organization_id: number;
  organization_name: string;
  created_at: string;
  updated_at: string;
}

export async function rebuildMigrationMappings() {
  console.log('🔧 Starting migration mapping rebuild...');
  
  try {
    // Step 1: Get all JARVIS customers
    console.log('📥 Fetching JARVIS customers...');
    const { data: jarvisCustomers, error: jarvisError } = await directSupabase
      .from('customers')
      .select('*');
    
    if (jarvisError) {
      throw new Error(`Failed to fetch JARVIS customers: ${jarvisError.message}`);
    }
    
    console.log(`Found ${jarvisCustomers?.length || 0} JARVIS customers`);
    
    // Step 2: Get QuoteDock customers from Edge Function
    console.log('📥 Fetching QuoteDock customers...');
    const accessToken = localStorage.getItem('sb-access-token');
    
    const response = await fetch('https://tqmqbyhtazbgsnqzybkx.supabase.co/functions/v1/client-customers', {
      headers: {
        'X-API-Key': 'GUY8QoG+vV6LlKdvKWC8IbVPFLnkmaj8zowt4alsqYE=',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch QuoteDock customers: ${response.statusText}`);
    }
    
    const quoteDockData = await response.json();
    const quoteDockCustomers: QuoteDockCustomer[] = quoteDockData.success ? quoteDockData.data : [];
    console.log(`Found ${quoteDockCustomers.length} QuoteDock customers`);
    
    // Step 3: Get existing mappings to avoid duplicates
    console.log('📊 Checking existing mappings...');
    const { data: existingMappings, error: mappingError } = await directSupabase
      .from('customer_migration_mapping')
      .select('quotedock_customer_id, jarvis_customer_id');
    
    if (mappingError) {
      throw new Error(`Failed to fetch existing mappings: ${mappingError.message}`);
    }
    
    const existingQuoteDockIds = new Set(existingMappings?.map(m => m.quotedock_customer_id) || []);
    const existingJarvisIds = new Set(existingMappings?.map(m => m.jarvis_customer_id) || []);
    
    console.log(`Found ${existingMappings?.length || 0} existing mappings`);
    
    // Step 4: Match JARVIS customers to QuoteDock customers
    console.log('🔍 Matching customers...');
    const matches = [];
    const matchStrategies = {
      email: 0,
      phone: 0,
      name: 0,
      organization: 0
    };
    
    for (const jarvisCustomer of jarvisCustomers || []) {
      // Skip if already mapped
      if (existingJarvisIds.has(jarvisCustomer.id)) {
        continue;
      }
      
      let match = null;
      
      // Strategy 1: Match by email (most reliable)
      if (jarvisCustomer.email && !match) {
        match = quoteDockCustomers.find((qd: QuoteDockCustomer) => 
          qd.email && qd.email.toLowerCase() === jarvisCustomer.email.toLowerCase()
        );
        if (match) matchStrategies.email++;
      }
      
      // Strategy 2: Match by phone number
      if (jarvisCustomer.phone_number && !match) {
        const cleanJarvisPhone = jarvisCustomer.phone_number.replace(/\D/g, '');
        match = quoteDockCustomers.find((qd: QuoteDockCustomer) => {
          if (!qd.phone_number) return false;
          const cleanQdPhone = qd.phone_number.replace(/\D/g, '');
          return cleanJarvisPhone === cleanQdPhone;
        });
        if (match) matchStrategies.phone++;
      }
      
      // Strategy 3: Match by name (less reliable)
      if (jarvisCustomer.name && !match) {
        match = quoteDockCustomers.find((qd: QuoteDockCustomer) => 
          qd.name && qd.name.toLowerCase() === jarvisCustomer.name.toLowerCase()
        );
        if (match) matchStrategies.name++;
      }
      
      // Strategy 4: Match by organization
      if (jarvisCustomer.organization_name && !match) {
        match = quoteDockCustomers.find((qd: QuoteDockCustomer) => 
          qd.organization_name && 
          qd.organization_name.toLowerCase() === jarvisCustomer.organization_name.toLowerCase()
        );
        if (match) matchStrategies.organization++;
      }
      
      if (match && !existingQuoteDockIds.has(match.id)) {
        matches.push({
          jarvisCustomer,
          quoteDockCustomer: match,
          matchedBy: jarvisCustomer.email && match.email ? 'email' :
                    jarvisCustomer.phone_number && match.phone_number ? 'phone' :
                    jarvisCustomer.name && match.name ? 'name' : 'organization'
        });
        existingQuoteDockIds.add(match.id); // Prevent duplicate matches
      }
    }
    
    console.log('🎯 Matching results:');
    console.table(matchStrategies);
    console.log(`Total matches found: ${matches.length}`);
    
    // Step 5: Create missing mappings
    console.log('💾 Creating missing mappings...');
    let createdCount = 0;
    const createResults = [];
    
    for (const match of matches) {
      try {
        const { error } = await directSupabase.rpc('create_customer_mapping', {
          quotedock_id: match.quoteDockCustomer.id,
          jarvis_id: match.jarvisCustomer.id,
          name: match.jarvisCustomer.name,
          email: match.jarvisCustomer.email || null,
          phone: match.jarvisCustomer.phone_number || null
        });
        
        if (error) {
          console.error(`❌ Failed to create mapping for ${match.jarvisCustomer.name}:`, error.message);
          createResults.push({
            customer: match.jarvisCustomer.name,
            success: false,
            error: error.message,
            matchedBy: match.matchedBy
          });
        } else {
          createdCount++;
          console.log(`✅ Created mapping: ${match.jarvisCustomer.name} (${match.matchedBy})`);
          createResults.push({
            customer: match.jarvisCustomer.name,
            success: true,
            matchedBy: match.matchedBy
          });
        }
      } catch (error) {
        console.error(`❌ Error creating mapping for ${match.jarvisCustomer.name}:`, error);
        createResults.push({
          customer: match.jarvisCustomer.name,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          matchedBy: match.matchedBy
        });
      }
    }
    
    // Step 6: Verify final mapping count
    console.log('📊 Verifying final mapping count...');
    const { count: finalMappingCount, error: countError } = await directSupabase
      .from('customer_migration_mapping')
      .select('*', { count: 'exact', head: true });
    
    if (!countError) {
      console.log(`✅ Final mapping count: ${finalMappingCount}`);
    }
    
    // Summary
    console.log('✅ Migration mapping rebuild completed!');
    console.log(`📊 Summary:`);
    console.log(`   - JARVIS customers: ${jarvisCustomers?.length || 0}`);
    console.log(`   - QuoteDock customers: ${quoteDockCustomers.length}`);
    console.log(`   - Existing mappings: ${existingMappings?.length || 0}`);
    console.log(`   - New matches found: ${matches.length}`);
    console.log(`   - New mappings created: ${createdCount}`);
    console.log(`   - Final total mappings: ${finalMappingCount || 'unknown'}`);
    
    return {
      success: true,
      jarvisCustomers: jarvisCustomers?.length || 0,
      quoteDockCustomers: quoteDockCustomers.length,
      existingMappings: existingMappings?.length || 0,
      newMatches: matches.length,
      newMappingsCreated: createdCount,
      finalMappingCount: finalMappingCount || 0,
      matchStrategies,
      createResults
    };
    
  } catch (error) {
    console.error('❌ Migration mapping rebuild failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).rebuildMappings = rebuildMigrationMappings;
